// Small formatting + type-detection helpers shared by ResultsTable and
// KpiTiles. Heuristic only — we don't get column types from the backend,
// so we infer from the values we see.

export type CellKind = "number" | "date" | "boolean" | "text";

export function detectColumnKind(values: unknown[]): CellKind {
  let numeric = 0;
  let date = 0;
  let bool = 0;
  let seen = 0;
  for (const v of values) {
    if (v === null || v === undefined || v === "") continue;
    seen++;
    if (typeof v === "number" || (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v))) {
      numeric++;
      continue;
    }
    if (typeof v === "boolean" || v === "true" || v === "false") {
      bool++;
      continue;
    }
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2})?/.test(v)) {
      date++;
      continue;
    }
  }
  if (seen === 0) return "text";
  if (bool / seen >= 0.9) return "boolean";
  if (numeric / seen >= 0.9) return "number";
  if (date / seen >= 0.9) return "date";
  return "text";
}

export function formatNumber(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return String(n);
  if (Math.abs(num) >= 1000) {
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (!Number.isInteger(num)) return num.toLocaleString(undefined, { maximumFractionDigits: 3 });
  return num.toString();
}

export function formatCompact(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return String(n);
  if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(num) >= 1_000)     return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return formatNumber(num);
}

export function formatDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  // Short ISO-ish for dates, localized for timestamps.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return d.toLocaleDateString();
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatCell(v: unknown, kind: CellKind): string {
  if (v === null || v === undefined || v === "") return "—";
  switch (kind) {
    case "number":  return formatNumber(v as number | string);
    case "date":    return typeof v === "string" ? formatDate(v) : String(v);
    case "boolean": return v === true || v === "true" || v === 1 ? "Yes" : "No";
    default:        return String(v);
  }
}

export function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = now - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Validation heuristic: every number the narrator mentions in the summary
 * should appear either in the row count or somewhere in the rendered
 * rows. The backend enforces this too (Narrator post-check), but the
 * UI can surface a "validated" badge when we can confirm locally.
 */
export function validateNarrativeNumbers(
  summary: string | undefined,
  rows: unknown[][] | undefined,
  rowCount: number | undefined,
): { ok: boolean; missing: string[] } {
  if (!summary) return { ok: true, missing: [] };
  const mentioned = Array.from(summary.matchAll(/-?\b\d[\d,]*(?:\.\d+)?\b/g)).map((m) =>
    m[0].replaceAll(",", ""),
  );
  if (mentioned.length === 0) return { ok: true, missing: [] };
  const rowNumbers = new Set<string>();
  if (rowCount !== undefined) rowNumbers.add(String(rowCount));
  for (const r of rows ?? []) {
    for (const v of r) {
      if (v === null || v === undefined) continue;
      const s = String(v).replaceAll(",", "");
      if (/^-?\d+(\.\d+)?$/.test(s)) rowNumbers.add(s);
    }
  }
  const missing = mentioned.filter((n) => !rowNumbers.has(n));
  return { ok: missing.length === 0, missing };
}
